import React, { useState, useEffect, useMemo } from "react";
import ReactSelect, { components } from "react-select";
import { 
  getTopScrapReasons, 
  getCustomerFleets,
  getScrapReasonFleetWise,
  getTopScrapReasonsTyreData,
  getScrapReasonFleetWiseTyreData,
  downloadTopScrapReasonsTyres,
  downloadScrapReasonFleetWiseTyres,
} from "../../apis/scrappedCasingTyres.service";
import { CustomBarChart } from "../../common/CustomBarChart";
import { ScrapReasonsDonutChart } from "../../common/ScrapReasonsDonutChart";
import Spinner from "../Spinner";
import { AiOutlineCheck } from "react-icons/ai";
import { MONTH_OPTIONS } from "../../utils/scrapConstants";
import CustomDetailModal from "./CustomDetailModal";
import { BiCloudDownload } from "react-icons/bi";
import toast from "react-hot-toast";
import { exportScrapCasingTyresExcel } from "../../utils/exportExcel";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const styles = {
    container: (noCard) => `${noCard ? "w-full" : "w-full max-w-[1400px]"}`,
    loaderWrapper: "absolute inset-0 z-10 flex justify-center items-center bg-white/50",
    filterContainer: "flex flex-wrap items-center gap-2 justify-end",
    fleetSelectWrapper: "w-[170px]",
    dateFilterRow: "flex gap-2",
    validationSelect: "w-[130px]",
    topNSelect: "w-[95px]",
    optionRow: "flex items-center justify-between gap-2 overflow-hidden",
    optionLabel: "truncate",
    checkIcon: "text-blue-600 flex-shrink-0",
    valueContainer: { 
        maxWidth: '180px', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center'
    }
};

const filterInputStyle = {
    height: "36px",
    border: "1px solid #858585",
    borderRadius: "6px",
    padding: "6px 10px 6px 8px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "white",
    fontSize: "12px",
    fontWeight: "400",
    fontFamily: "'Inter', sans-serif",
    color: "#2C2C2C",
    cursor: "pointer",
    outline: "none",
    textAlign: "center"
};

// Custom Option component with checkbox/check icon
const CustomOption = (props) => {
  return (
    <components.Option {...props}>
      <div className={styles.optionRow}>
        <span className={styles.optionLabel}>{props.label}</span>
        {props.isSelected && <AiOutlineCheck className={styles.checkIcon} />}
      </div>
    </components.Option>
  );
};

function getMonthsInDateRange(dateFromStr, dateToStr) {
  if (!dateFromStr || !dateToStr) return MONTH_OPTIONS;
  const start = new Date(`${dateFromStr}T12:00:00`);
  const end = new Date(`${dateToStr}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const out = [];
  let y = start.getFullYear();
  let m = start.getMonth();
  const endY = end.getFullYear();
  const endM = end.getMonth();
  while (y < endY || (y === endY && m <= endM)) {
    const monthNum = m + 1;
    const label = MONTH_OPTIONS.find((x) => x.value === monthNum)?.label || "";
    out.push({ value: monthNum, label });
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return out;
}

const CustomValueContainer = ({ children, ...props }) => {
    const { getValue, hasValue } = props;
    const nbValues = getValue().length;
    
    if (!hasValue || nbValues <= 1) {
        return (
            <components.ValueContainer {...props}>
                <div style={styles.valueContainer}>
                    {children}
                </div>
            </components.ValueContainer>
        );
    }

    return (
        <components.ValueContainer {...props}>
            <div style={styles.valueContainer}>
                {Array.isArray(children[0]) ? children[0][0] : children[0]}
                <span className="text-xs font-semibold text-gray-500 ml-2 whitespace-nowrap">
                    +{nbValues - 1} more
                </span>
                {children[1]}
            </div>
        </components.ValueContainer>
    );
};

const ScrapAnalysisFleetWise = ({ rowsPerPage = 5, noCard = false }) => {
  const MAX_VISIBLE_BARS = 7;
  const [fleets, setFleets] = useState([]);
  const [selectedFleets, setSelectedFleets] = useState([]);
  const [isValidated, setIsValidated] = useState(null); 
  const [topN, setTopN] = useState("5");

  const defaultDateFrom = new Date();
  defaultDateFrom.setMonth(defaultDateFrom.getMonth() - 3);
  const todayDate = dayjs();
  const [dateFrom, setDateFrom] = useState(defaultDateFrom.toISOString().split("T")[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split("T")[0]);

  const handleDateFromChange = (value) => {
    if (!value) {
      setDateFrom("");
      return;
    }
    if (!value.isValid()) return;
    if (value.isAfter(todayDate, "day")) return;
    if (dateTo && value.isAfter(dayjs(dateTo), "day")) return;
    setDateFrom(value.format("YYYY-MM-DD"));
  };

  const handleDateToChange = (value) => {
    if (!value) {
      setDateTo("");
      return;
    }
    if (!value.isValid()) return;
    if (value.isAfter(todayDate, "day")) return;
    if (dateFrom && value.isBefore(dayjs(dateFrom), "day")) return;
    setDateTo(value.format("YYYY-MM-DD"));
  };

  const [viewMode, setViewMode] = useState("reasons");
  const [selectedReason, setSelectedReason] = useState(null);
  const [selectedReasonId, setSelectedReasonId] = useState(null);

  const [rawData, setRawData] = useState([]);
  const [fullChartData, setFullChartData] = useState({ labels: [], datasets: [] });
  const [loading, setLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    const fetchFleets = async () => {
      try {
        const response = await getCustomerFleets();
        if (response?.data) {
          setFleets(response.data.map(f => ({ value: f._id, label: f.fleetName })));
        }
      } catch (error) {
        console.error("Error fetching fleets:", error);
      }
    };
    fetchFleets();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
        const payload = {};
        if (topN) payload.topN = topN;
        if (dateFrom) payload.dateFrom = dateFrom;
        if (dateTo) payload.dateTo = dateTo;
        if (selectedFleets.length > 0) payload.fleets = selectedFleets.map(f => f.value);
        if (isValidated !== null) payload.isValidated = isValidated;

        let response;
        if (viewMode === "reasons") {
            response = await getTopScrapReasons(payload);
        } else {
            payload.scrapReasonId = selectedReasonId;
            response = await getScrapReasonFleetWise(payload);
        }

        if (response?.data && Array.isArray(response.data)) {
          setRawData(response.data);
          processChartData(response.data);
        }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (data) => {
    const activeMonths = getMonthsInDateRange(dateFrom, dateTo);
    const abbreviate = activeMonths.length > 6;
    const monthColors = {
      january: "#FDBA74", february: "#F9A8D4", march: "#FDE68A",
      april: "#A7F3D0", may: "#BAE6FD", june: "#FECACA",
      july: "#BFDBFE", august: "#FEF3C7", september: "#DDD6FE",
      october: "#FDBA74", november: "#F9A8D4", december: "#FDE68A"
    };

    const labels = data.map(item => {
      let name = viewMode === "reasons" 
        ? (item.reason === "------" ? "Other" : item.reason)
        : (item.fleetName || item.hubName || "Unknown");
      const words = name.split(" ");
      if (words.length > 2) {
        const result = [];
        for (let i = 0; i < words.length; i += 2) {
          result.push(words.slice(i, i + 2).join(" "));
        }
        return result;
      }
      return words;
    });

    const datasets = activeMonths.map(mObj => {
      const mKey = mObj.label.toLowerCase();
      return {
        label: abbreviate ? mObj.label.slice(0, 3) : mObj.label,
        data: data.map(item => {
           // Sum only active months to ensure 100% stacking within the selected range
           const calculatedTotal = activeMonths.reduce((sum, m) => sum + (item[m.label.toLowerCase()] || 0), 0);
           const val = item[mKey] || 0;
           return calculatedTotal > 0 ? (val / calculatedTotal) * 100 : 0;
        }),
        counts: data.map(item => item[mKey] || 0),
        reasonPercents: data.map(item => {
           const calculatedTotal = activeMonths.reduce((sum, m) => sum + (item[m.label.toLowerCase()] || 0), 0);
           const val = item[mKey] || 0;
           return calculatedTotal > 0 ? (val / calculatedTotal) * 100 : 0;
        }),
        backgroundColor: monthColors[mKey] || "#CBD5E1",
      };
    });

    setFullChartData({ labels, datasets });
  };

  useEffect(() => {
    fetchData();
  }, [topN, dateFrom, dateTo, selectedFleets, isValidated, viewMode]);

  const pieChartData = useMemo(() => {
    if (viewMode !== "reasons") return [];
    const activeMonths = getMonthsInDateRange(dateFrom, dateTo);
    return rawData
      .map((item, rawIndex) => {
        const name = item.reason === "------" ? "Other" : item.reason;
        const value =
          typeof item.count === "number" && !Number.isNaN(item.count)
            ? item.count
            : activeMonths.reduce(
                (s, m) => s + (Number(item[m.label.toLowerCase()]) || 0),
                0
              );
        return {
          name,
          value,
          id: item._id || item.reasonId,
          rawIndex,
        };
      })
      .filter((d) => d.value > 0);
  }, [viewMode, rawData, dateFrom, dateTo]);

  const handlePieSliceClick = (slice) => {
    const rawItem = rawData[slice.rawIndex];
    if (!rawItem) return;
    setSelectedReason(rawItem.reason === "------" ? "Other" : rawItem.reason);
    setSelectedReasonId(rawItem._id || rawItem.reasonId);
    setViewMode("fleetHub");
  };

  const handlePieTooltipActionClick = (slice) => {
    setSelectedDetail({
      title: `${slice.name} (${slice.value})`,
      resource: slice.name,
      resourceId: slice.id,
      type: "Total",
      value: slice.value,
      month: null,
    });
  };

  useEffect(() => {
    if (selectedDetail) {
      const fetchDetailData = async () => {
        setModalLoading(true);
        try {
          const payload = {
            topN,
            dateFrom,
            dateTo,
          };
          if (isValidated !== null) payload.isValidated = isValidated;
          if (selectedFleets.length > 0) payload.fleets = selectedFleets.map(f => f.value);

          let response;
          if (viewMode === "reasons") {
            payload.scrapReason = selectedDetail.resourceId;
            response = await getTopScrapReasonsTyreData(payload);
          } else {
            payload.month = selectedDetail.month;
            payload.scrapReasonId = selectedReasonId;
            payload.fleetId = selectedDetail.resourceId;
            response = await getScrapReasonFleetWiseTyreData(payload);
          }

          if (response?.success && response?.data) {
            setModalData(response.data);
          }
        } catch (error) {
          console.error("Error fetching modal data:", error);
        } finally {
          setModalLoading(false);
        }
      };
      fetchDetailData();
    } else {
      setModalData([]);
    }
  }, [selectedDetail]);

  const resolveDownloadTyreRows = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.data)) return response.data;
    return [];
  };

  const handleDownloadTyresData = async () => {
    if (!selectedDetail) return;
    setDownloadLoading(true);
    try {
      const base = {
        topN,
        dateFrom,
        dateTo,
      };
      if (isValidated !== null) base.isValidated = isValidated;
      if (selectedFleets.length > 0) base.fleets = selectedFleets.map((f) => f.value);

      let response;
      if (viewMode === "reasons") {
        response = await downloadTopScrapReasonsTyres({
          ...base,
          scrapReason: selectedDetail.resourceId,
        });
      } else {
        response = await downloadScrapReasonFleetWiseTyres({
          ...base,
          month: selectedDetail.month,
          scrapReasonId: selectedReasonId,
          fleetId: selectedDetail.resourceId,
        });
      }

      const list = resolveDownloadTyreRows(response);
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      const safeLabel = String(selectedDetail.resource || "detail").replace(/\s+/g, "_");
      const monthPart =
        viewMode === "fleetHub" && selectedDetail.month != null ? `_m${selectedDetail.month}` : "";
      exportScrapCasingTyresExcel(
        list,
        `scrap-reasons-tyres_${viewMode === "reasons" ? "top" : "fleet-wise"}_${safeLabel}${monthPart}`,
        { tyreStatus: 'scrap' }
      );
      toast.success("File downloaded");
    } catch (error) {
      console.error("Download scrap reasons tyres failed:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleXAxisCategoryClick = (event, elements) => {
    if (viewMode !== "reasons" || !elements?.length) return;
    const index = elements[0]?.index;
    if (index == null || index < 0) return;
    const rawItem = rawData[index];
    if (!rawItem) return;
    setSelectedReason(rawItem.reason === "------" ? "Other" : rawItem.reason);
    setSelectedReasonId(rawItem._id || rawItem.reasonId);
    setViewMode("fleetHub");
  };

  const breadcrumbs = [
    { label: "Top Scrap Reasons", active: viewMode === "reasons", onClick: () => {
        setViewMode("reasons");
        setSelectedReason(null);
        setSelectedReasonId(null);
    }},
    ...(viewMode === "fleetHub" ? [{ label: selectedReason === "------" ? "Other" : selectedReason, active: true }] : [])
  ];

  const reactSelectCustomStyles = {
    control: (provided) => ({
      ...provided,
      height: '36px',
      minHeight: '36px',
      borderRadius: '6px',
      borderColor: "#858585",
      fontSize: "12px",
      fontWeight: "400",
      color: "#2C2C2C",
      "&:hover": { borderColor: "#858585" }
    }),
    valueContainer: (provided) => ({ ...provided, padding: '0 6px', flexWrap: 'nowrap' }),
    singleValue: (provided) => ({ ...provided, color: "#2C2C2C", fontSize: "12px" }),
    multiValue: (provided) => ({ ...provided, fontSize: "12px" }),
    multiValueLabel: (provided) => ({ ...provided, fontSize: "12px" }),
    input: (provided) => ({ ...provided, fontSize: "12px" }),
    option: (provided) => ({ ...provided, fontSize: "12px" }),
    dropdownIndicator: (provided) => ({ ...provided, padding: '0 6px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    placeholder: (provided) => ({ ...provided, color: "#2C2C2C", opacity: 0.6, fontSize: "12px" }),
    menu: (provided) => ({
      ...provided,
      width: '100%',
      borderRadius: '6px',
      marginTop: '4px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      zIndex: 20
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '200px',
      overflowX: 'hidden',
      overflowY: 'auto',
      padding: '4px 0',
      '&::-webkit-scrollbar': {
        width: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#CBD5E1',
        borderRadius: '2px',
      },
    })
  };

  const validationOptions = [
    { value: "all", label: "All" },
    { value: "true", label: "Validated" },
    { value: "false", label: "Not Validated" },
  ];

  const topNOptions = [
    { value: "5", label: "Top 5" },
    { value: "10", label: "Top 10" },
    { value: "15", label: "Top 15" },
  ];

  const ActionFilters = (
    <div className={styles.filterContainer}>
        <div className={styles.fleetSelectWrapper}>
            <ReactSelect
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={fleets}
                components={{ Option: CustomOption, ValueContainer: CustomValueContainer }}
                value={selectedFleets}
                onChange={setSelectedFleets}
                placeholder="Select Fleets"
                styles={reactSelectCustomStyles}
            />
        </div>
        <div className={styles.dateFilterRow}>
            <div style={filterInputStyle}>
                <span className="text-[#2C2C2C] opacity-40 text-xs">From:</span>
                <DatePicker
                  value={dateFrom ? dayjs(dateFrom) : null}
                  onChange={handleDateFromChange}
                  format="DD/MM/YYYY"
                  maxDate={dateTo ? dayjs(dateTo) : todayDate}
                  disableFuture
                  slotProps={{
                    actionBar: { actions: ["clear", "today"] },
                    textField: {
                      variant: "standard",
                      sx: {
                        width: 100,
                        "& input": { fontSize: 12, padding: "9px 0" },
                      },
                      InputProps: { disableUnderline: true },
                    },
                  }}
                />
            </div>
            <div style={filterInputStyle}>
                <span className="text-[#2C2C2C] opacity-40 text-xs">To:</span>
                <DatePicker
                  value={dateTo ? dayjs(dateTo) : null}
                  onChange={handleDateToChange}
                  format="DD/MM/YYYY"
                  minDate={dateFrom ? dayjs(dateFrom) : undefined}
                  maxDate={todayDate}
                  disableFuture
                  slotProps={{
                    actionBar: { actions: ["clear", "today"] },
                    textField: {
                      variant: "standard",
                      sx: {
                        width: 100,
                        "& input": { fontSize: 12, padding: "9px 0" },
                      },
                      InputProps: { disableUnderline: true },
                    },
                  }}
                />
            </div>
        </div>
        <div className={styles.validationSelect}>
            <ReactSelect
                options={validationOptions}
                value={validationOptions.find(o => o.value === (isValidated === null ? "all" : isValidated.toString()))}
                onChange={(option) => {
                    const val = option?.value ?? "all";
                    setIsValidated(val === "all" ? null : val === "true");
                }}
                isSearchable={false}
                placeholder="Validation"
                styles={reactSelectCustomStyles}
            />
        </div>
        <div className={styles.topNSelect}>
            <ReactSelect
                options={topNOptions}
                value={topNOptions.find(o => o.value === String(topN))}
                onChange={(option) => setTopN(option?.value ?? "5")}
                isSearchable={false}
                placeholder="Top N"
                styles={reactSelectCustomStyles}
            />
        </div>
    </div>
  );

  const dynamicTitle =
    viewMode === "reasons" ? `Top ${topN} Scrap Reasons` : "Scrap Reasons";

  const baseColumns = [
    { key: "zone", label: "Zone" },
    { key: "region", label: "Region" },
    { key: "hubName", label: "Hub Name" },
    { key: "stencilNo", label: "Stencil No." },
    { key: "tubeType", label: "Tube Type/Tube less" },
    { key: "scrapReason", label: "Scrap Reason" },
  ];
  const detailColumns = [
    ...baseColumns,
    ...(isValidated !== true  ? [{ key: "lastInspDate",    label: "Last Insp. Date"   }] : []),
    ...(isValidated !== false ? [{ key: "validationDate",  label: "Validation Date"   }] : []),
    ...(isValidated !== false ? [{ key: "validatedBy",     label: "Validated By"      }] : []),
  ];

  return (
    <div className={styles.container(noCard)}>
       {loading && viewMode !== "reasons" && <div className={styles.loaderWrapper}><Spinner /></div>}
       {viewMode === "reasons" ? (
          <ScrapReasonsDonutChart
            title={dynamicTitle}
            titleTextSize="24px"
            subTitle=" (fleet-wise)"
            subTitleStyle={{ fontStyle: "italic", fontWeight: 400, fontSize: "18px", color: "black" }}
            breadcrumbs={breadcrumbs}
            boxPadding={0}
            noBorder={true}
            noShadow={true}
            topRightAction={ActionFilters}
            data={pieChartData}
            onSliceClick={handlePieSliceClick}
            onTooltipActionClick={handlePieTooltipActionClick}
            chartHeight={500}
            minLabelPercent={0}
            loading={loading}
          />
       ) : (
          <CustomBarChart
            title={dynamicTitle}
            titleTextSize="24px"
            subTitle=" (fleet-wise)"
            subTitleStyle={{ fontStyle: "italic", fontWeight: 400, fontSize: "18px", color: "black" }}
            breadcrumbs={breadcrumbs}
            boxPadding={0}
            noBorder={true}
            noShadow={true}
            headerLayout="stack"
            labels={fullChartData.labels}
            datasets={fullChartData.datasets}
            xAxisTitle="Fleets & Hubs"
            yAxisMax={100}
            yAxisStepSize={20}
            yAxisTitle="Percentage of Scrapped Tyres"
            yAxisFormatter={(value) => `${value}%`}
            yAxisTitlePosition="-top-[15px] left-[20px]"
            stacked={true}
            barThickness={76}
            maxVisibleCategories={8}
            hideTotalOnTop={true}
            onBarClick={(event, elements) => {
              if (!elements?.length) return;
              const localIndex = elements[0]?.index;
              const datasetIndex = elements[0]?.datasetIndex;
              const item = rawData[localIndex];
              const datasetLabel = fullChartData.datasets[datasetIndex]?.label;

              const activeMonths = getMonthsInDateRange(dateFrom, dateTo);
              const abbreviate = activeMonths.length > 6;
              const monthObj = activeMonths.find((m) => {
                const label = abbreviate ? m.label.slice(0, 3) : m.label;
                return label === datasetLabel;
              });

              const labelPrefix = item.fleetName || item.hubName || "Unknown";
              const countValue = fullChartData.datasets[datasetIndex]?.counts[localIndex];

              setSelectedDetail({
                title: `${labelPrefix} - ${datasetLabel} (${countValue})`,
                resource: labelPrefix,
                resourceId: item._id || item.reasonId || item.hubId || item.fleetId,
                type: datasetLabel,
                value: countValue,
                month: monthObj?.value,
              });
            }}
            interactiveTooltip={true}
            allowBarClick={false}
            enableHorizontalScroll={true}
            minCategoryWidth={95}
            topRightAction={ActionFilters}
            legendItems={(() => {
              const activeMonths = getMonthsInDateRange(dateFrom, dateTo);
              const abbreviate = activeMonths.length > 6;
              const monthColors = {
                january: "#FDBA74",
                february: "#F9A8D4",
                march: "#FDE68A",
                april: "#A7F3D0",
                may: "#BAE6FD",
                june: "#FECACA",
                july: "#BFDBFE",
                august: "#FEF3C7",
                september: "#DDD6FE",
                october: "#FDBA74",
                november: "#F9A8D4",
                december: "#FDE68A",
              };
              return activeMonths.map((m) => ({
                label: abbreviate ? m.label.slice(0, 3) : m.label,
                color: monthColors[m.label.toLowerCase()] || "#CBD5E1",
              }));
            })()}
            datalabelsFormatter={(value, context) => {
              const ds = context.dataset;
              if (
                !ds.reasonPercents ||
                !ds.reasonPercents[context.dataIndex] ||
                !ds.counts ||
                ds.counts[context.dataIndex] === undefined
              )
                return "";

              const reasonPercent = ds.reasonPercents[context.dataIndex];
              const count = ds.counts[context.dataIndex];
              if (reasonPercent < 5) return "";
              return [`${count}`, `${reasonPercent.toFixed(2)}%`];
            }}
          />
       )}
        
        <CustomDetailModal
          isOpen={!!selectedDetail}
          onClose={() => setSelectedDetail(null)}
          title={selectedDetail?.title}
          modalWidth="85vw"
          filterComponent={
            <button 
              type="button"
              className="flex items-center gap-2 bg-[#22C55E] text-white px-5 py-2.5 rounded-xl hover:bg-green-600 transition-all shadow-sm hover:shadow-md font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={downloadLoading || modalLoading || !selectedDetail}
              onClick={handleDownloadTyresData}
            >
              <BiCloudDownload size={20} className={downloadLoading ? "animate-pulse" : ""} />
              <span>{downloadLoading ? "Preparing…" : "Download Data"}</span>
            </button>
          }
          columns={detailColumns}
          loading={modalLoading}
          data={
            modalData.map((d, idx) => ({
              id: idx,
              zone: d.zone || "-",
              region: d.region || "-",
              hubName: d["Hub Name"] || d.hubName || "-",
              stencilNo: d.stencilNo || "-",
              lastInspDate: d.lastInspectionDate
                ? new Date(d.lastInspectionDate).toLocaleDateString('en-GB')
                : "-",
              tubeType: d.productType || d.tubeType || "-",
              scrapReason: d.scrapReason || "-",
              validationDate: d.tyreValidationDate 
                ? new Date(d.tyreValidationDate).toLocaleDateString('en-GB') 
                : "-",
              validatedBy: d.validatedBy || "-",
            }))
          }
        />
    </div>
  );
};

export default ScrapAnalysisFleetWise;
